import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonNameDialog from './PersonNameDialog';
import { getPeopleNames, deletePersonName } from '../../services/userService';
import { NAME_GRID_COLUMNS } from '../../constants/personNameFormConstants';

const PersonNamesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [names, setNames] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadNames = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleNames(personId),
      onSuccess: (data) => setNames(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingName(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingName(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingName(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadNames();
  }, [handleFormClose, loadNames]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete "${row.FullName || row.LastName}"?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deletePersonName(row.ptsNameID),
            onSuccess: () => {
              dispatch(notify('Name deleted successfully', 'success'));
              loadNames();
            },
          });
        },
        'Delete Name'
      );
    },
    [dispatch, requestConfirm, loadNames]
  );

  useEffect(() => {
    loadNames();
  }, [loadNames]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Name"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={names}
        columns={NAME_GRID_COLUMNS}
        getRowId={(row) => row.ptsNameID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Names',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonNameDialog
          open={formOpen}
          personId={personId}
          name={editingName}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonNamesTab;
